import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiPlus, FiEdit, FiTrash2, FiEye, FiEyeOff } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { bannerService, Banner } from '../../services/bannerService';
import { getImageUrl } from '../../config';
import { showConfirm } from '../../utils/sweetAlert';

// Importar los componentes estilizados compartidos
import {
  ActionButton,
  ActionButtons,
  Button,
  ButtonGroup,
  Form,
  FormGroup,
  IconButton,
  Input,
  Label,
  Modal,
  ModalContent,
  Table,
  TableCell,
  TableHeader,
  TableWrapper
} from '../shared/StyledComponents'; // ✅ Ruta correcta

// Interfaces simplificadas
interface SimpleBanner {
  id: string;
  desktop_image_url: string;
  mobile_image_url: string;
  active: boolean;
  created_at: string;
  updated_at: string;
  link?: string; // <-- Agregar campo opcional para el link
}

interface SimpleBannerFormData {
  active: boolean;
  desktop_image?: File;
  mobile_image?: File;
  link?: string; // <-- Agregar campo para el link
}

// Componentes de iconos
const Plus = FiPlus as React.ComponentType;
const Edit = FiEdit as React.ComponentType;
const Trash = FiTrash2 as React.ComponentType;
const Eye = FiEye as React.ComponentType;
const EyeOff = FiEyeOff as React.ComponentType;

const BannersTab: React.FC = () => {
  const [banners, setBanners] = useState<SimpleBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState<SimpleBanner | null>(null);
  const [formData, setFormData] = useState<SimpleBannerFormData>({
    active: true
  });

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      setLoading(true);
      const response = await bannerService.getAllBanners();
      setBanners(response.banners || []);
    } catch (error) {
      console.error('Error loading banners:', error);
      toast.error('Error al cargar banners');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type, checked, files, value } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'file') {
      const file = files?.[0];
      setFormData(prev => ({ ...prev, [name]: file }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value })); // <-- Soportar campos de texto
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const submitFormData = new FormData();
      submitFormData.append('active', formData.active.toString());
      if (formData.link) {
        submitFormData.append('link', formData.link);
      }
      if (formData.desktop_image) {
        submitFormData.append('desktop_image', formData.desktop_image);
      }
      if (formData.mobile_image) {
        submitFormData.append('mobile_image', formData.mobile_image);
      }

      if (editingBanner) {
        await bannerService.updateBanner(editingBanner.id, submitFormData);
        toast.success('Banner actualizado correctamente');
      } else {
        if (!formData.desktop_image) {
          toast.error('La imagen de escritorio es obligatoria');
          return;
        }
        await bannerService.createBanner(submitFormData);
        toast.success('Banner creado correctamente');
      }
      
      setShowModal(false);
      resetForm();
      loadBanners();
    } catch (error) {
      console.error('Error saving banner:', error);
      toast.error('Error al guardar banner');
    }
  };

  const handleAddBanner = () => {
    setEditingBanner(null);
    resetForm();
    setShowModal(true);
  };

  const handleEditBanner = (banner: SimpleBanner) => {
    setEditingBanner(banner);
    setFormData({
      active: banner.active,
      link: banner.link || '' // <-- Cargar el link si existe
    });
    setShowModal(true);
  };

  const handleToggleActive = async (banner: SimpleBanner) => {
    try {
      const formData = new FormData();
      formData.append('active', (!banner.active).toString());
      
      await bannerService.updateBanner(banner.id, formData);
      toast.success(`Banner ${!banner.active ? 'activado' : 'desactivado'} correctamente`);
      loadBanners();
    } catch (error) {
      console.error('Error toggling banner:', error);
      toast.error('Error al cambiar estado del banner');
    }
  };

  const handleDeleteBanner = async (bannerId: string) => {
    const confirmed = await showConfirm('¿Estás seguro de que quieres eliminar este banner?');
    if (confirmed) {
      try {
        await bannerService.deleteBanner(bannerId);
        setBanners(banners.filter(b => b.id !== bannerId));
        toast.success('Banner eliminado correctamente');
      } catch (error) {
        console.error('Error deleting banner:', error);
        toast.error('Error al eliminar banner');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      active: true,
      link: '' // <-- Resetear el link
    });
  };

  if (loading) {
    return <div>Cargando banners...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Gestión de Banners</h2>
        <ActionButton onClick={handleAddBanner}>
          <Plus /> Agregar Banner
        </ActionButton>
      </div>

      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <TableHeader>ID</TableHeader>
              <TableHeader>Imagen Desktop</TableHeader>
              <TableHeader>Imagen Mobile</TableHeader>
              <TableHeader>Estado</TableHeader>
              <TableHeader>Acciones</TableHeader>
            </tr>
          </thead>
          <tbody>
            {banners.map((banner) => (
            <tr key={banner.id}>
              <TableCell>{banner.id}</TableCell>
              <TableCell>
                {banner.desktop_image_url && (
                  <img
                    src={getImageUrl(banner.desktop_image_url)}
                    alt="Desktop"
                    style={{ width: '100px', height: '50px', objectFit: 'cover' }}
                  />
                )}
              </TableCell>
              <TableCell>
                {banner.mobile_image_url && (
                  <img
                    src={getImageUrl(banner.mobile_image_url)}
                    alt="Mobile"
                    style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                  />
                )}
              </TableCell>
              <TableCell>
                <span style={{ 
                  color: banner.active ? '#27ae60' : '#e74c3c',
                  fontWeight: 'bold'
                }}>
                  {banner.active ? 'Activo' : 'Inactivo'}
                </span>
              </TableCell>
              <TableCell>
                <ActionButtons>
                  <IconButton
                    variant="edit"
                    onClick={() => handleToggleActive(banner)}
                    title={banner.active ? 'Desactivar' : 'Activar'}
                  >
                    {banner.active ? <EyeOff /> : <Eye />}
                  </IconButton>
                  <IconButton
                    variant="edit"
                    onClick={() => handleEditBanner(banner)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    variant="delete"
                    onClick={() => handleDeleteBanner(banner.id)}
                  >
                    <Trash />
                  </IconButton>
                </ActionButtons>
              </TableCell>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableWrapper>

      {/* Modal simplificado */}
      <Modal show={showModal}>
        <ModalContent>
          <h2>{editingBanner ? 'Editar Banner' : 'Agregar Banner'}</h2>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Imagen Desktop *</Label>
              <div style={{
                fontSize: '13px',
                color: '#6c757d',
                fontWeight: '600',
                marginBottom: '8px',
                padding: '8px 12px',
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                🖥️ Tamaño recomendado: 1912 × 40 píxeles
              </div>
              <Input
                type="file"
                name="desktop_image"
                onChange={handleInputChange}
                accept="image/*"
                required={!editingBanner}
              />
              {editingBanner && editingBanner.desktop_image_url && (
                <div style={{ marginTop: '10px' }}>
                  <img
                    src={getImageUrl(editingBanner.desktop_image_url)}
                    alt="Vista previa desktop"
                    style={{ maxWidth: '200px', maxHeight: '100px' }}
                  />
                </div>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Imagen Mobile (opcional)</Label>
              <div style={{
                fontSize: '13px',
                color: '#6c757d',
                fontWeight: '600',
                marginBottom: '8px',
                padding: '8px 12px',
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                📱 Tamaño recomendado: 500 × 60 píxeles
              </div>
              <Input
                type="file"
                name="mobile_image"
                onChange={handleInputChange}
                accept="image/*"
              />
              {editingBanner && editingBanner.mobile_image_url && (
                <div style={{ marginTop: '10px' }}>
                  <img
                    src={getImageUrl(editingBanner.mobile_image_url)}
                    alt="Vista previa mobile"
                    style={{ maxWidth: '200px', maxHeight: '100px' }}
                  />
                </div>
              )}
            </FormGroup>

            <FormGroup>
              <Label>URL de redireccionamiento (opcional)</Label>
              <Input
                type="text"
                name="link"
                value={formData.link || ''}
                onChange={handleInputChange}
                placeholder="https://tusitio.com/promo"
              />
            </FormGroup>

            <FormGroup>
              <Label>
                <Input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleInputChange}
                  style={{ width: 'auto', marginRight: '0.5rem' }}
                />
                Activo
              </Label>
            </FormGroup>

            <ButtonGroup>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowModal(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" variant="primary">
                {editingBanner ? 'Actualizar' : 'Agregar'}
              </Button>
            </ButtonGroup>
          </Form>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default BannersTab;